/**
 * What sits above the message box in a team conversation:
 *  - the pill that says who you are talking to ("Talking to Budget Planner ✕"), always visible while it applies;
 *  - the strip of agents the team has shared, one click to start talking to one (so nobody has to ask "which agents are there?").
 */
import React from 'react';
import { Bot, X, Globe, Lock } from 'lucide-react';
import { talkTo } from './useAgentSession.js';
import { kTokens } from './teamUi.jsx';
import { useRoster } from './TeamAgentCards.jsx';

const T = {
  surface: 'var(--lyra-color-surface)', surface2: 'var(--lyra-color-surface-2)', border: 'var(--lyra-color-border)',
  ink: 'var(--lyra-color-text)', ink3: 'var(--lyra-color-text-muted)', ink4: 'var(--app-text-3)',
  accent: 'var(--lyra-color-primary)', accentLight: 'var(--app-primary-soft)', accentMid: 'var(--app-primary-soft-2)',
};

const hhmm = (d) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** How the conversation is covered, in the person's words. */
export function sessionNote(session, grants = [], meId, covers = []) {
  if (!session) return '';
  const mine = session.ownerUserId && String(session.ownerUserId) === String(meId);
  if (mine) return 'yours';
  const g = (grants || []).find((x) => String(x.agentId) === String(session.agentId));
  const access = session.access === 'open' ? 'open to the team' : g ? `approved until ${hhmm(g.expiresAt)}` : `${session.owner || 'its owner'} approves first`;
  if (session.payer !== 'requester') return access;
  // Whoever uses it pays: say so, and how much of the person's own cover is left.
  const c = (covers || []).find((x) => String(x.agentId) === String(session.agentId));
  return `${access} · ${c ? `you cover the tokens, ${kTokens(c.tokensLeft)} left` : 'you cover the tokens (you confirm first)'}`;
}

export function AgentSessionPill({ session, grants, covers, meId, onEnd }) {
  if (!session) return null;
  return (
    <div role="status" aria-label={`Talking to ${session.name}`}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 16px', background: T.accentLight, borderTop: `1px solid ${T.accentMid}`, fontSize: 12.5, color: T.ink }}>
      <Bot size={14} color="var(--lyra-color-primary)" />
      <span>Talking to <b>{session.name}</b> <span style={{ color: T.ink3 }}>· {sessionNote(session, grants, meId, covers)}</span></span>
      <span style={{ color: T.ink4, fontSize: 11.5 }}>Your messages go to it until you end this. @mention someone to ask them once.</span>
      <button type="button" onClick={onEnd} aria-label={`Stop talking to ${session.name}`} title="Back to the whole team"
        style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, border: `1px solid ${T.accentMid}`, background: T.surface, color: T.ink, borderRadius: 8, padding: '3px 9px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
        <X size={12} /> End
      </button>
    </div>
  );
}

const MAX_CHIPS = 8;

/** The team's shared agents as chips. Hidden while you are already talking to one, or when nothing is shared. */
export function TeamAgentStrip({ spaceId, meId, hidden }) {
  const roster = useRoster(spaceId, false);
  const agents = (roster?.agents || []).filter((a) => a.status === 'active');
  if (hidden || agents.length === 0) return null;
  const shown = agents.slice(0, MAX_CHIPS);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px', borderTop: `1px solid ${T.border}`, overflowX: 'auto', whiteSpace: 'nowrap' }}>
      <span style={{ fontSize: 11.5, color: T.ink4, flexShrink: 0 }}>Team agents</span>
      {shown.map((a) => {
        const isOpen = a.access === 'open';
        const mine = String(a.ownerUserId) === String(meId);
        return (
          <button key={a._id} type="button"
            title={`${a.description || a.name}\n${mine ? 'Yours' : isOpen ? `Open: ${a.ownerName} shared it with everyone` : `${a.ownerName} approves first`}\nTokens: ${a.payer === 'requester' ? (mine ? 'whoever uses it' : 'you cover them, after you confirm') : (mine ? 'you' : a.ownerName)}`}
            onClick={() => talkTo(spaceId, { agentId: a.agentId, name: a.name, access: a.access, payer: a.payer || 'owner', owner: a.ownerName, ownerUserId: a.ownerUserId })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0, border: `1px solid ${isOpen || mine ? T.accentMid : T.border}`, background: isOpen || mine ? T.accentLight : T.surface, color: T.ink, borderRadius: 99, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
            {mine || isOpen ? <Globe size={11} color="var(--lyra-color-primary)" /> : <Lock size={11} color="currentColor" />}
            {a.name}
          </button>
        );
      })}
      {agents.length > MAX_CHIPS && <span style={{ fontSize: 11.5, color: T.ink4, flexShrink: 0 }}>+{agents.length - MAX_CHIPS} more: type @ to find them</span>}
    </div>
  );
}
